import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CreditCard, Settings, Tag, Users, Zap, RefreshCw, Plus, Trash2,
  CheckCircle, XCircle, Clock, AlertTriangle, ChevronDown, ChevronUp,
  DollarSign, Percent, Calendar, Copy, ExternalLink, Shield, BarChart3,
  Package, Gift, TrendingUp, Eye, EyeOff, X
} from "lucide-react";

const API = "/api";

const INTERVAL_LABELS = {
  day: { label: "Diario", labelEs: "día", color: "text-blue-400", bg: "bg-blue-500/20" },
  week: { label: "Semanal", labelEs: "semana", color: "text-purple-400", bg: "bg-purple-500/20" },
  month: { label: "Mensual", labelEs: "mes", color: "text-green-400", bg: "bg-green-500/20" },
  year: { label: "Anual", labelEs: "año", color: "text-yellow-400", bg: "bg-yellow-500/20" },
};

const STATUS_COLORS = {
  active: "text-green-400 bg-green-500/20",
  trialing: "text-blue-400 bg-blue-500/20",
  past_due: "text-yellow-400 bg-yellow-500/20",
  canceled: "text-red-400 bg-red-500/20",
  incomplete: "text-orange-400 bg-orange-500/20",
  unpaid: "text-red-400 bg-red-500/20",
};

function StatCard({ icon: Icon, label, value, color = "text-red-400", loading }) {
  return (
    <div className="glass-panel p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color.replace("text-", "bg-").replace("-400", "-500/20")}`}>
          <Icon size={18} className={color} />
        </div>
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      {loading ? (
        <div className="h-8 w-16 bg-white/10 rounded animate-pulse" />
      ) : (
        <p className="text-2xl font-bold text-white">{value}</p>
      )}
    </div>
  );
}

function ConfigTab({ onSaved }) {
  const [cfg, setCfg] = useState({ secret_key: "", publishable_key: "", webhook_secret: "", fee_percent: 3.0 });
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/stripe/config`)
      .then(r => setCurrent(r.data))
      .catch(() => {});
  }, []);

  const save = async () => {
    if (!cfg.secret_key.startsWith("sk_")) {
      toast.error("La clave secreta debe empezar con sk_");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/stripe/config`, cfg);
      toast.success("Configuración guardada correctamente");
      const r = await axios.get(`${API}/admin/stripe/config`);
      setCurrent(r.data);
      setCfg(c => ({ ...c, secret_key: "", webhook_secret: "" }));
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {current?.configured && (
        <div className="glass-panel p-4 border border-green-500/30 bg-green-500/5">
          <div className="flex items-center gap-3">
            <CheckCircle size={20} className="text-green-400" />
            <div>
              <p className="text-green-300 font-medium">Stripe conectado</p>
              <p className="text-gray-400 text-sm">Clave activa: {current.secret_key_hint} · Fee actual: {current.fee_percent}%</p>
            </div>
          </div>
        </div>
      )}

      <div className="glass-panel p-6 space-y-5">
        <h3 className="text-white font-semibold text-lg flex items-center gap-2">
          <Shield size={18} className="text-red-400" /> Claves de API de Stripe
        </h3>

        <div>
          <label className="block text-gray-400 text-sm mb-2">Clave Secreta (Secret Key)*</label>
          <div className="relative">
            <input
              type={showSecret ? "text" : "password"}
              value={cfg.secret_key}
              onChange={e => setCfg(c => ({ ...c, secret_key: e.target.value }))}
              placeholder={current?.configured ? "Nueva clave (dejar vacío para no cambiar)" : "sk_test_... o sk_live_..."}
              className="input-glass w-full pr-10"
            />
            <button onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
              {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="text-gray-600 text-xs mt-1">Obtén tu clave en dashboard.stripe.com → Developers → API keys</p>
        </div>

        <div>
          <label className="block text-gray-400 text-sm mb-2">Clave Publicable (Publishable Key)</label>
          <input
            type="text"
            value={cfg.publishable_key}
            onChange={e => setCfg(c => ({ ...c, publishable_key: e.target.value }))}
            placeholder={current?.publishable_key || "pk_test_... o pk_live_..."}
            className="input-glass w-full"
          />
        </div>

        <div>
          <label className="block text-gray-400 text-sm mb-2">Webhook Secret</label>
          <div className="relative">
            <input
              type={showWebhook ? "text" : "password"}
              value={cfg.webhook_secret}
              onChange={e => setCfg(c => ({ ...c, webhook_secret: e.target.value }))}
              placeholder={current?.webhook_configured ? "Configurado ✓" : "whsec_..."}
              className="input-glass w-full pr-10"
            />
            <button onClick={() => setShowWebhook(!showWebhook)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
              {showWebhook ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="text-gray-600 text-xs mt-1">Webhook URL: <code className="text-blue-400">/api/admin/stripe/webhook</code></p>
        </div>

        <div>
          <label className="block text-gray-400 text-sm mb-2">Fee OkBot (%)</label>
          <div className="flex items-center gap-3">
            <input
              type="number" min="0" max="50" step="0.5"
              value={cfg.fee_percent}
              onChange={e => setCfg(c => ({ ...c, fee_percent: parseFloat(e.target.value) }))}
              className="input-glass w-32"
            />
            <span className="text-gray-400 text-sm">% sobre precios OpenRouter de modelos de paga</span>
          </div>
        </div>

        <button
          onClick={save}
          disabled={saving || (!cfg.secret_key && !cfg.publishable_key && !cfg.webhook_secret)}
          className="btn-primary flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <RefreshCw size={16} className="animate-spin" /> : <CheckCircle size={16} />}
          Guardar Configuración
        </button>
      </div>

      <div className="glass-panel p-5 bg-blue-500/5 border border-blue-500/20">
        <h4 className="text-blue-300 font-medium mb-3 flex items-center gap-2"><ExternalLink size={14} /> Recursos de Stripe</h4>
        <ul className="space-y-1 text-sm text-gray-400">
          <li>• <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">API Keys → dashboard.stripe.com/apikeys</a></li>
          <li>• <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">Webhooks → dashboard.stripe.com/webhooks</a></li>
          <li>• <a href="https://dashboard.stripe.com/test/dashboard" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">Dashboard de pruebas → dashboard.stripe.com/test</a></li>
        </ul>
      </div>
    </div>
  );
}

function PlanesTab() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({
    name: "", description: "", interval: "month", amount: "", currency: "usd",
    trial_days: 0, features: ""
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/stripe/products`)
      .then(r => setPlans(r.data.products || []))
      .catch(() => toast.error("Error cargando planes"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const [seeding, setSeeding] = useState(false);

  const create = async () => {
    if (!form.name || !form.amount) { toast.error("Nombre y precio son requeridos"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/stripe/products`, {
        ...form,
        amount: parseFloat(form.amount),
        trial_days: parseInt(form.trial_days) || 0,
        currency: "mxn",
        features: form.features ? form.features.split("\n").filter(f => f.trim()) : []
      });
      toast.success("Plan creado en Stripe");
      setShowForm(false);
      setForm({ name: "", description: "", interval: "month", amount: "", currency: "mxn", trial_days: 0, features: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error creando plan");
    } finally {
      setSaving(false);
    }
  };

  const seedOkbotPlans = async () => {
    if (!window.confirm("¿Crear los 4 planes oficiales de OkBot en Stripe (mensual + anual = 8 planes)? Esto puede tardar unos segundos.")) return;
    setSeeding(true);
    try {
      const r = await axios.post(`${API}/admin/stripe/seed-okbot-plans`);
      const { created, errors } = r.data;
      if (created.length > 0) toast.success(`${created.length} planes creados correctamente en MXN`);
      if (errors.length > 0) toast.error(`${errors.length} planes fallaron: ${errors[0].error}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error creando planes");
    } finally {
      setSeeding(false);
    }
  };

  const archive = async (id, name) => {
    if (!window.confirm(`¿Archivar plan "${name}"? Los suscriptores actuales no se verán afectados.`)) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/admin/stripe/products/${id}`);
      toast.success("Plan archivado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error archivando plan");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-white font-semibold">Planes de Suscripción</h3>
          <p className="text-gray-500 text-sm mt-0.5">Planes en pesos mexicanos (MXN)</p>
        </div>
        <div className="flex gap-2">
          <button onClick={seedOkbotPlans} disabled={seeding} className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-medium disabled:opacity-50 transition-colors">
            {seeding ? <RefreshCw size={15} className="animate-spin" /> : <Zap size={15} />}
            Crear Planes OkBot
          </button>
          <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2">
            <Plus size={16} /> Nuevo Plan
          </button>
        </div>
      </div>

      {showForm && (
        <div className="glass-panel p-6 border border-red-500/30">
          <h4 className="text-white font-medium mb-5 flex items-center gap-2"><Package size={16} className="text-red-400" /> Crear Plan en Stripe</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1.5">Nombre del Plan*</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ej: Plan Pro Mensual" className="input-glass w-full" />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1.5">Intervalo de Cobro*</label>
              <select value={form.interval} onChange={e => setForm(f => ({ ...f, interval: e.target.value }))} className="input-glass w-full">
                <option value="day">Diario (cada día)</option>
                <option value="week">Semanal (cada semana)</option>
                <option value="month">Mensual (cada mes)</option>
                <option value="year">Anual (cada año)</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1.5">Precio (MXN)*</label>
              <div className="relative">
                <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input type="number" min="0" step="0.01" value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="9.99" className="input-glass w-full pl-8" />
              </div>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1.5">Período de Prueba (días)</label>
              <input type="number" min="0" max="365" value={form.trial_days}
                onChange={e => setForm(f => ({ ...f, trial_days: e.target.value }))}
                placeholder="0 = sin período de prueba" className="input-glass w-full" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-400 text-sm mb-1.5">Descripción</label>
              <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Descripción del plan para los usuarios" className="input-glass w-full" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-400 text-sm mb-1.5">Características (una por línea)</label>
              <textarea value={form.features} onChange={e => setForm(f => ({ ...f, features: e.target.value }))}
                placeholder={"Chat ilimitado\nModelos premium\nSoporte prioritario"}
                rows={3} className="input-glass w-full resize-none" />
            </div>
          </div>

          {form.amount && form.interval && (
            <div className="bg-white/5 rounded-xl p-3 mb-4 text-sm text-gray-400">
              Resumen: <span className="text-white font-medium">${parseFloat(form.amount || 0).toLocaleString('es-MX')} MXN/{INTERVAL_LABELS[form.interval]?.labelEs}</span>
              {parseInt(form.trial_days) > 0 && <span className="text-blue-400 ml-2">· {form.trial_days} días de prueba gratis</span>}
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={create} disabled={saving} className="btn-primary flex items-center gap-2">
              {saving ? <RefreshCw size={16} className="animate-spin" /> : <CreditCard size={16} />}
              Crear en Stripe
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-white/5 text-gray-400 rounded-xl hover:bg-white/10">Cancelar</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw size={20} className="animate-spin text-gray-500" /></div>
      ) : plans.length === 0 ? (
        <div className="glass-panel p-12 text-center">
          <Package size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 mb-1">No hay planes creados aún</p>
          <p className="text-gray-600 text-sm">Crea tu primer plan de suscripción</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {plans.map(plan => (
            <div key={plan.id} className="glass-panel p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-white font-semibold">{plan.name}</h4>
                    {plan.prices.map(pr => pr.interval && (
                      <span key={pr.id} className={`text-xs px-2.5 py-1 rounded-full font-medium ${INTERVAL_LABELS[pr.interval]?.bg} ${INTERVAL_LABELS[pr.interval]?.color}`}>
                        {INTERVAL_LABELS[pr.interval]?.label}
                      </span>
                    ))}
                  </div>
                  {plan.description && <p className="text-gray-400 text-sm mb-3">{plan.description}</p>}
                  <div className="flex flex-wrap gap-4 text-sm">
                    {plan.prices.map(pr => (
                      <div key={pr.id} className="flex items-center gap-2">
                        <DollarSign size={14} className="text-green-400" />
                        <span className="text-white font-bold">${pr.amount.toFixed(2)}</span>
                        <span className="text-gray-500">/{pr.interval || "one-time"}</span>
                        {parseInt(pr.trial_period_days) > 0 && (
                          <span className="text-blue-400 text-xs bg-blue-500/20 px-2 py-0.5 rounded-full">{pr.trial_period_days}d trial</span>
                        )}
                      </div>
                    ))}
                  </div>
                  {plan.metadata?.features && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {plan.metadata.features.split(",").map((f, i) => (
                        <span key={i} className="text-xs bg-white/5 text-gray-400 px-2 py-0.5 rounded-full">{f.trim()}</span>
                      ))}
                    </div>
                  )}
                  <p className="text-gray-600 text-xs mt-2 font-mono">{plan.id}</p>
                </div>
                <button
                  onClick={() => archive(plan.id, plan.name)}
                  disabled={deleting === plan.id}
                  className="p-2 hover:bg-red-500/20 text-gray-600 hover:text-red-400 rounded-lg transition-colors ml-4"
                >
                  {deleting === plan.id ? <RefreshCw size={16} className="animate-spin" /> : <Trash2 size={16} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CuponesTab() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({
    name: "", duration: "once", duration_months: 3, percent_off: "", amount_off: "",
    discount_type: "percent", currency: "usd", max_redemptions: "", code: ""
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/stripe/coupons`)
      .then(r => setCoupons(r.data.coupons || []))
      .catch(() => toast.error("Error cargando cupones"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name) { toast.error("El nombre es requerido"); return; }
    if (!form.percent_off && !form.amount_off) { toast.error("Especifica el descuento (% o monto fijo)"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, duration: form.duration, currency: form.currency,
        ...(form.code && { code: form.code }),
        ...(form.max_redemptions && { max_redemptions: parseInt(form.max_redemptions) }),
        ...(form.duration === "repeating" && { duration_months: parseInt(form.duration_months) }),
        ...(form.discount_type === "percent"
          ? { percent_off: parseFloat(form.percent_off) }
          : { amount_off: parseFloat(form.amount_off) })
      };
      const r = await axios.post(`${API}/admin/stripe/coupons`, payload);
      toast.success(`Cupón "${form.name}" creado${r.data.promo_code_id ? ` con código "${form.code}"` : ""}`);
      setShowForm(false);
      setForm({ name: "", duration: "once", duration_months: 3, percent_off: "", amount_off: "", discount_type: "percent", currency: "usd", max_redemptions: "", code: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error creando cupón");
    } finally {
      setSaving(false);
    }
  };

  const deleteCoupon = async (id, name) => {
    if (!window.confirm(`¿Eliminar cupón "${name}"?`)) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/admin/stripe/coupons/${id}`);
      toast.success("Cupón eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error eliminando cupón");
    } finally {
      setDeleting(null);
    }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copiado"); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-semibold">Cupones y Códigos Promocionales</h3>
          <p className="text-gray-500 text-sm mt-0.5">Descuentos por porcentaje o monto fijo</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Nuevo Cupón
        </button>
      </div>

      {showForm && (
        <div className="glass-panel p-6 border border-red-500/30">
          <h4 className="text-white font-medium mb-5 flex items-center gap-2"><Tag size={16} className="text-red-400" /> Crear Cupón de Descuento</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1.5">Nombre del Cupón*</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ej: Descuento Bienvenida" className="input-glass w-full" />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1.5">Código Promocional (opcional)</label>
              <input type="text" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="Ej: BIENVENIDA20" className="input-glass w-full font-mono" />
              <p className="text-gray-600 text-xs mt-1">El código que usarán los usuarios</p>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1.5">Tipo de Descuento</label>
              <select value={form.discount_type} onChange={e => setForm(f => ({ ...f, discount_type: e.target.value }))} className="input-glass w-full">
                <option value="percent">Porcentaje (%)</option>
                <option value="amount">Monto fijo (USD)</option>
              </select>
            </div>
            <div>
              {form.discount_type === "percent" ? (
                <>
                  <label className="block text-gray-400 text-sm mb-1.5">Porcentaje de Descuento*</label>
                  <div className="relative">
                    <Percent size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input type="number" min="1" max="100" step="1" value={form.percent_off}
                      onChange={e => setForm(f => ({ ...f, percent_off: e.target.value }))}
                      placeholder="20" className="input-glass w-full pl-8" />
                  </div>
                </>
              ) : (
                <>
                  <label className="block text-gray-400 text-sm mb-1.5">Monto de Descuento (USD)*</label>
                  <div className="relative">
                    <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input type="number" min="0.01" step="0.01" value={form.amount_off}
                      onChange={e => setForm(f => ({ ...f, amount_off: e.target.value }))}
                      placeholder="5.00" className="input-glass w-full pl-8" />
                  </div>
                </>
              )}
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1.5">Duración</label>
              <select value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} className="input-glass w-full">
                <option value="once">Una vez</option>
                <option value="repeating">Repetir N meses</option>
                <option value="forever">Para siempre</option>
              </select>
            </div>
            {form.duration === "repeating" && (
              <div>
                <label className="block text-gray-400 text-sm mb-1.5">Duración en Meses</label>
                <input type="number" min="1" value={form.duration_months}
                  onChange={e => setForm(f => ({ ...f, duration_months: e.target.value }))}
                  className="input-glass w-full" />
              </div>
            )}
            <div>
              <label className="block text-gray-400 text-sm mb-1.5">Máx. de Usos (opcional)</label>
              <input type="number" min="1" value={form.max_redemptions}
                onChange={e => setForm(f => ({ ...f, max_redemptions: e.target.value }))}
                placeholder="Sin límite" className="input-glass w-full" />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={create} disabled={saving} className="btn-primary flex items-center gap-2">
              {saving ? <RefreshCw size={16} className="animate-spin" /> : <Gift size={16} />}
              Crear Cupón
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-white/5 text-gray-400 rounded-xl hover:bg-white/10">Cancelar</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw size={20} className="animate-spin text-gray-500" /></div>
      ) : coupons.length === 0 ? (
        <div className="glass-panel p-12 text-center">
          <Tag size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 mb-1">No hay cupones creados</p>
          <p className="text-gray-600 text-sm">Crea cupones de descuento para tus usuarios</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {coupons.map(coupon => (
            <div key={coupon.id} className="glass-panel p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h4 className="text-white font-semibold">{coupon.name}</h4>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${coupon.valid ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-500"}`}>
                      {coupon.valid ? "Activo" : "Inactivo"}
                    </span>
                    <span className="text-xs bg-white/5 text-gray-400 px-2.5 py-1 rounded-full">
                      {coupon.duration === "once" ? "Una vez" : coupon.duration === "forever" ? "Para siempre" : `${coupon.duration_in_months} meses`}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-red-400 mb-2">
                    {coupon.percent_off ? `${coupon.percent_off}% OFF` : `$${coupon.amount_off?.toFixed(2)} OFF`}
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                    <span>{coupon.times_redeemed} usos{coupon.max_redemptions ? ` / ${coupon.max_redemptions} máx.` : ""}</span>
                    {coupon.promo_codes?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {coupon.promo_codes.map(pc => (
                          <button key={pc.id} onClick={() => copy(pc.code)}
                            className="flex items-center gap-1 font-mono text-xs bg-white/10 px-2.5 py-1 rounded-full hover:bg-white/20 text-white">
                            <Copy size={11} /> {pc.code}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-gray-700 text-xs mt-1 font-mono">{coupon.id}</p>
                </div>
                <button
                  onClick={() => deleteCoupon(coupon.id, coupon.name)}
                  disabled={deleting === coupon.id}
                  className="p-2 hover:bg-red-500/20 text-gray-600 hover:text-red-400 rounded-lg transition-colors ml-4"
                >
                  {deleting === coupon.id ? <RefreshCw size={16} className="animate-spin" /> : <Trash2 size={16} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SuscripcionesTab() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");
  const [processing, setProcessing] = useState(null);
  const [trialModal, setTrialModal] = useState(null);
  const [trialDays, setTrialDays] = useState(7);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/stripe/subscriptions?status=${statusFilter}`)
      .then(r => setSubs(r.data.subscriptions || []))
      .catch(() => toast.error("Error cargando suscripciones"))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const cancelSub = async (id) => {
    if (!window.confirm("¿Cancelar esta suscripción al final del período de pago?")) return;
    setProcessing(id);
    try {
      await axios.post(`${API}/admin/stripe/subscriptions/${id}/cancel`);
      toast.success("Suscripción marcada para cancelar al final del período");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error cancelando suscripción");
    } finally {
      setProcessing(null);
    }
  };

  const grantTrial = async (id) => {
    setProcessing(id);
    try {
      await axios.post(`${API}/admin/stripe/subscriptions/${id}/grant-trial`, { trial_days: trialDays });
      toast.success(`Período de prueba extendido ${trialDays} días`);
      setTrialModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error extendiendo período de prueba");
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (ts) => ts ? new Date(ts * 1000).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-white font-semibold">Suscripciones</h3>
          <p className="text-gray-500 text-sm mt-0.5">{subs.length} suscripción{subs.length !== 1 ? "es" : ""} encontradas</p>
        </div>
        <div className="flex gap-2">
          {["active", "trialing", "past_due", "canceled", "all"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${statusFilter === s ? "bg-red-500 text-white" : "bg-white/5 text-gray-400 hover:bg-white/10"}`}>
              {s === "active" ? "Activas" : s === "trialing" ? "En prueba" : s === "past_due" ? "Vencidas" : s === "canceled" ? "Canceladas" : "Todas"}
            </button>
          ))}
          <button onClick={load} className="p-2 bg-white/5 text-gray-400 hover:bg-white/10 rounded-xl">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {trialModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="glass-panel p-6 w-full max-w-sm border border-blue-500/30">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-semibold flex items-center gap-2"><Clock size={16} className="text-blue-400" /> Extender Período de Prueba</h4>
              <button onClick={() => setTrialModal(null)} className="text-gray-500 hover:text-gray-300"><X size={18} /></button>
            </div>
            <p className="text-gray-400 text-sm mb-4">Usuario: <span className="text-white">{trialModal.customer_email || trialModal.id}</span></p>
            <div className="mb-5">
              <label className="block text-gray-400 text-sm mb-2">Días de prueba a otorgar</label>
              <input type="number" min="1" max="365" value={trialDays} onChange={e => setTrialDays(parseInt(e.target.value))} className="input-glass w-full" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => grantTrial(trialModal.id)} disabled={processing === trialModal.id} className="btn-primary flex items-center gap-2 flex-1 justify-center">
                {processing === trialModal.id ? <RefreshCw size={16} className="animate-spin" /> : <Gift size={16} />}
                Otorgar {trialDays} días
              </button>
              <button onClick={() => setTrialModal(null)} className="px-4 py-2 bg-white/5 text-gray-400 rounded-xl hover:bg-white/10">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw size={20} className="animate-spin text-gray-500" /></div>
      ) : subs.length === 0 ? (
        <div className="glass-panel p-12 text-center">
          <Users size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 mb-1">No hay suscripciones con este filtro</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {subs.map(sub => (
            <div key={sub.id} className="glass-panel p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <p className="text-white font-medium">{sub.customer_email || "Sin email"}</p>
                    {sub.customer_name && <span className="text-gray-500 text-sm">({sub.customer_name})</span>}
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLORS[sub.status] || "text-gray-400 bg-gray-500/20"}`}>
                      {sub.status}
                    </span>
                    {sub.cancel_at_period_end && (
                      <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full">Cancela al vencer</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                    {sub.plan_name && <span className="text-gray-300"><Package size={12} className="inline mr-1" />{sub.plan_name}</span>}
                    {sub.interval && <span>{INTERVAL_LABELS[sub.interval]?.label || sub.interval}</span>}
                    <span><Calendar size={12} className="inline mr-1" />Vence: {formatDate(sub.current_period_end)}</span>
                    {sub.trial_end && <span className="text-blue-400"><Clock size={12} className="inline mr-1" />Trial hasta: {formatDate(sub.trial_end)}</span>}
                  </div>
                  <p className="text-gray-700 text-xs mt-1 font-mono">{sub.id}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => setTrialModal(sub)}
                    className="p-2 hover:bg-blue-500/20 text-gray-600 hover:text-blue-400 rounded-lg transition-colors"
                    title="Otorgar período de prueba"
                  ><Clock size={16} /></button>
                  {sub.status === "active" && !sub.cancel_at_period_end && (
                    <button
                      onClick={() => cancelSub(sub.id)}
                      disabled={processing === sub.id}
                      className="p-2 hover:bg-red-500/20 text-gray-600 hover:text-red-400 rounded-lg transition-colors"
                      title="Cancelar suscripción"
                    >
                      {processing === sub.id ? <RefreshCw size={16} className="animate-spin" /> : <XCircle size={16} />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TokenPricingTab() {
  const [pricing, setPricing] = useState([]);
  const [fee, setFee] = useState(3.0);
  const [newFee, setNewFee] = useState(3.0);
  const [loading, setLoading] = useState(true);
  const [savingFee, setSavingFee] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/stripe/token-pricing`)
      .then(r => {
        setPricing(r.data.pricing || []);
        setFee(r.data.fee_percent);
        setNewFee(r.data.fee_percent);
      })
      .catch(() => toast.error("Error cargando precios"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveFee = async () => {
    setSavingFee(true);
    try {
      await axios.post(`${API}/admin/stripe/token-pricing/fee`, { fee_percent: newFee });
      toast.success(`Fee actualizado a ${newFee}%`);
      setFee(newFee);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error actualizando fee");
    } finally {
      setSavingFee(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-white font-semibold">Precios por Token — Modelos de Paga</h3>
          <p className="text-gray-500 text-sm mt-0.5">Tarifas de OpenRouter + fee OkBot. Precio en USD por millón de tokens.</p>
        </div>
      </div>

      <div className="glass-panel p-5 border border-yellow-500/20 bg-yellow-500/5">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Percent size={18} className="text-yellow-400" />
            <div>
              <p className="text-white font-medium">Fee OkBot sobre precios OpenRouter</p>
              <p className="text-gray-500 text-sm">Se aplica a todos los modelos de paga</p>
            </div>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <input
              type="number" min="0" max="50" step="0.5" value={newFee}
              onChange={e => setNewFee(parseFloat(e.target.value))}
              className="input-glass w-24 text-center"
            />
            <span className="text-gray-400">%</span>
            <button onClick={saveFee} disabled={savingFee || newFee === fee} className="btn-primary flex items-center gap-2 disabled:opacity-50">
              {savingFee ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              Aplicar
            </button>
          </div>
        </div>
      </div>

      <div className="glass-panel overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <div className="grid grid-cols-4 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div>Modelo</div>
            <div className="text-right">Input base (OpenRouter)</div>
            <div className="text-right">Output base (OpenRouter)</div>
            <div className="text-right text-yellow-400">Con fee OkBot ({fee}%)</div>
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-8"><RefreshCw size={20} className="animate-spin text-gray-500" /></div>
        ) : (
          <div className="divide-y divide-white/5">
            {pricing.map(p => (
              <div key={p.model_id} className="p-4 hover:bg-white/5 transition-colors">
                <div className="grid grid-cols-4 gap-4 items-center">
                  <div>
                    <p className="text-white text-sm font-medium">{p.name}</p>
                    <p className="text-gray-600 text-xs font-mono">{p.model_id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-300 text-sm">${p.input_base.toFixed(4)}</p>
                    <p className="text-gray-600 text-xs">/M tokens</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-300 text-sm">${p.output_base.toFixed(4)}</p>
                    <p className="text-gray-600 text-xs">/M tokens</p>
                  </div>
                  <div className="text-right">
                    <p className="text-yellow-400 text-sm font-medium">
                      ${p.input_with_fee.toFixed(4)} / ${p.output_with_fee.toFixed(4)}
                    </p>
                    <p className="text-gray-600 text-xs">in / out</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass-panel p-4 bg-blue-500/5 border border-blue-500/20 text-sm text-gray-400">
        <p className="flex items-start gap-2">
          <AlertTriangle size={14} className="text-blue-400 mt-0.5 shrink-0" />
          <span>Los precios sin fee provienen de <a href="https://openrouter.ai/models" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">openrouter.ai/models</a>. Los modelos gratuitos (:free) no generan cargo por token. Solo los modelos de paga aplican estos precios.</span>
        </p>
      </div>
    </div>
  );
}

function DashboardTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/stripe/config`).then(r => setConfig(r.data)).catch(() => {});
    axios.get(`${API}/admin/stripe/dashboard`)
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (ts) => ts ? new Date(ts * 1000).toLocaleDateString("es-MX", { day: "2-digit", month: "short" }) : "—";

  if (!config?.configured) {
    return (
      <div className="glass-panel p-12 text-center">
        <CreditCard size={56} className="text-gray-600 mx-auto mb-4" />
        <h3 className="text-white font-semibold text-lg mb-2">Stripe no configurado</h3>
        <p className="text-gray-400 mb-1">Agrega tu clave secreta en la pestaña <strong>Configuración</strong></p>
        <p className="text-gray-600 text-sm">Necesitas una cuenta de Stripe para usar este módulo</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Clientes" value={stats?.customers_total ?? "—"} color="text-blue-400" loading={loading} />
        <StatCard icon={TrendingUp} label="Suscripciones activas" value={stats?.active_subscriptions ?? "—"} color="text-green-400" loading={loading} />
        <StatCard icon={Package} label="Planes" value={stats?.products_total ?? "—"} color="text-purple-400" loading={loading} />
        <StatCard icon={Tag} label="Cupones" value={stats?.coupons_total ?? "—"} color="text-yellow-400" loading={loading} />
      </div>

      {stats?.recent_subscriptions?.length > 0 && (
        <div className="glass-panel p-5">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2"><BarChart3 size={16} className="text-red-400" /> Suscripciones Recientes</h4>
          <div className="space-y-3">
            {stats.recent_subscriptions.map(s => (
              <div key={s.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <div>
                  <p className="text-white text-sm">{s.customer_email || "Sin email"}</p>
                  <p className="text-gray-500 text-xs">{s.plan} · {formatDate(s.created)}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[s.status] || "text-gray-400 bg-gray-500/20"}`}>{s.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="glass-panel p-5 bg-green-500/5 border border-green-500/20">
        <div className="flex items-center gap-3">
          <CheckCircle size={20} className="text-green-400" />
          <div>
            <p className="text-green-300 font-medium">Stripe conectado y operativo</p>
            <p className="text-gray-400 text-sm">Clave activa: {config.secret_key_hint} · Fee: {config.fee_percent}%</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StripeAdminPanel() {
  const [activeTab, setActiveTab] = useState("dashboard");

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "planes", label: "Planes", icon: Package },
    { id: "cupones", label: "Cupones", icon: Tag },
    { id: "suscripciones", label: "Suscripciones", icon: Users },
    { id: "tokens", label: "Precios Token", icon: Zap },
    { id: "config", label: "Configuración", icon: Settings },
  ];

  return (
    <div className="space-y-6">
      <div className="glass-panel p-5">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
            <CreditCard size={24} className="text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Stripe Billing</h2>
            <p className="text-gray-400 text-sm">Gestión completa de planes, cupones y suscripciones</p>
          </div>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-colors whitespace-nowrap text-sm font-medium ${
                activeTab === tab.id ? "bg-red-500 text-white" : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-200"
              }`}
            >
              <Icon size={15} /> {tab.label}
            </button>
          );
        })}
      </div>

      <div>
        {activeTab === "dashboard" && <DashboardTab />}
        {activeTab === "planes" && <PlanesTab />}
        {activeTab === "cupones" && <CuponesTab />}
        {activeTab === "suscripciones" && <SuscripcionesTab />}
        {activeTab === "tokens" && <TokenPricingTab />}
        {activeTab === "config" && <ConfigTab onSaved={() => setActiveTab("dashboard")} />}
      </div>
    </div>
  );
}
